import React, { useMemo, useContext } from "react";
import { graphql, Link } from "gatsby";
import {
  MenuFragmentFragment,
  NavStripBranchesFragmentFragment
} from "../../graphql-types";
import { getClickableAction } from "./Link";
import NavStrip, { NavStripProps } from "@unum/nav-strip";
import { LayoutContext } from "./Layout";

type Props = {
  data: {
    mainNavigation: MenuFragmentFragment;
    branches: NavStripBranchesFragmentFragment[];
    loginMenu?: MenuFragmentFragment;
  };
};

const mapItemsToNavigation = (
  items: MenuFragmentFragment["elements"]["items"]["value"]
): NavStripProps["secondaryMenu"] | undefined => {
  if (!items) {
    return;
  }

  return items.map((item) => {
    if (
      !(
        item.__typename === "kontent_item_external_link" ||
        item.__typename === "kontent_item_internal_link"
      )
    ) {
      return null;
    }

    return {
      label: item.elements.label.value,
      action: getClickableAction(item)
    };
  });
};

const mapBranchesToNavigation = (
  branches: NavStripBranchesFragmentFragment[],
  path: string = ""
): NavStripProps["primaryMenu"] => {
  return branches.map((branch) => {
    return {
      label: branch.elements.title.value,
      action: {
        model: "routerLink",
        linkComponent: Link,
        to: "/" + branch.elements.slug.value
      },
      isActive: path.startsWith("/" + branch.elements.slug.value)
    };
  });
};

const IntegratedNavStrip = ({ data }: Props) => {
  if (!data) {
    return;
  }

  const { location } = useContext(LayoutContext);
  const primaryMenuData = useMemo(
    () => mapBranchesToNavigation(data.branches, location?.pathname),
    [data.branches, location]
  );
  const secondaryMenuData = useMemo(
    () => mapItemsToNavigation(data.mainNavigation?.elements.items.value),
    [data.mainNavigation]
  );
  const loginMenuData = useMemo(
    () => mapItemsToNavigation(data.loginMenu?.elements.items.value),
    [data.loginMenu]
  );
  const loginMenu: NavStripProps["loginMenu"] = data.loginMenu
    ? {
        label: data.loginMenu.elements.label.value,
        links: loginMenuData
      }
    : undefined;

  if (!primaryMenuData || !secondaryMenuData) {
    return null;
  }

  return (
    <NavStrip
      primaryMenu={primaryMenuData}
      secondaryMenu={secondaryMenuData}
      loginMenu={loginMenu}
    />
  );
};

export default IntegratedNavStrip;

export const query = graphql`
  fragment MenuFragment on kontent_item_navigation {
    elements {
      label {
        value
      }
      items {
        value {
          ...LinkFragment
        }
      }
    }
  }
  fragment NavStripBranchesFragment on kontent_item_branch {
    elements {
      slug {
        value
      }
      title: main_name {
        value
      }
      subtitle {
        ...RichTextFragment
      }
    }
  }
`;
